import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '@/integrations/supabase/client';

// =====================================================================
// Blog — public listing of published blog_articles (Rankpill-generated
// or manually added). Public read via RLS policy.
// =====================================================================

const CATEGORY_COLOR = {
  'ai-trading':    'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  'discipline':    'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  'macro':         'bg-amber-500/20 text-amber-300 border-amber-500/40',
  'prop-firms':    'bg-purple-500/20 text-purple-300 border-purple-500/40',
  'analysis':      'bg-sky-500/20 text-sky-300 border-sky-500/40',
  default:         'bg-white/10 text-gray-300 border-white/20',
};

function categoryClass(cat) {
  const key = String(cat || '').toLowerCase().replace(/\s+/g, '-');
  return CATEGORY_COLOR[key] || CATEGORY_COLOR.default;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from('blog_articles')
        .select('id, slug, title, excerpt, meta_description, featured_image_url, category, author, reading_time_minutes, published_at')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(60);
      if (categoryFilter) q = q.eq('category', categoryFilter);
      const { data, error } = await q;
      if (cancelled) return;
      if (error) setError(error.message);
      else setArticles(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [categoryFilter]);

  const categories = Array.from(
    new Set(articles.map(a => a.category).filter(Boolean))
  ).sort();

  return (
    <>
      <Helmet>
        <title>Blog — SnapChart AI | Trading Insights, AI Chart Analysis, Prop Firm Strategies</title>
        <meta name="description" content="AI-powered chart analysis insights, prop firm strategies, and macro trading commentary. Learn when NOT to trade with SnapChart AI." />
        <meta property="og:title" content="SnapChart AI Blog" />
        <meta property="og:description" content="Trading discipline, AI chart analysis, and prop firm insights." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.snap-chart.ai/blog" />
        <link rel="canonical" href="https://www.snap-chart.ai/blog" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] to-[#14192b] text-white">
        {/* Header */}
        <div className="border-b border-white/10 bg-black/30 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-6 py-16 text-center">
            <Link to="/" className="text-cyan-400 hover:text-cyan-300 text-sm font-mono uppercase tracking-widest">
              ← Back to SnapChart AI
            </Link>
            <h1 className="mt-6 text-5xl md:text-6xl font-bold bg-gradient-to-r from-cyan-300 via-white to-emerald-300 bg-clip-text text-transparent">
              The Risk Officer Blog
            </h1>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
              AI-powered trading insights, macro analysis, and prop firm strategies. Learn when NOT to trade.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-12">
          {/* Category filter */}
          {categories.length > 0 && (
            <div className="mb-8 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setCategoryFilter(null)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest border transition-colors ${
                  categoryFilter === null
                    ? 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40'
                    : 'text-slate-400 border-white/10 hover:bg-white/[0.06]'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest border transition-colors ${
                    categoryFilter === cat
                      ? categoryClass(cat)
                      : 'text-slate-400 border-white/10 hover:bg-white/[0.06]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {loading && (
            <div className="text-center py-24 text-slate-400 font-mono">Loading articles…</div>
          )}
          {error && (
            <div className="text-center py-24 text-rose-400 font-mono">Error: {error}</div>
          )}
          {!loading && !error && articles.length === 0 && (
            <div className="text-center py-24 text-slate-400">
              <div className="text-xl mb-2">No articles yet.</div>
              <div className="text-sm text-slate-500">Check back soon — new insights are on the way.</div>
            </div>
          )}

          {!loading && articles.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(a => (
                <Link
                  key={a.id}
                  to={`/blog/${a.slug}`}
                  className="group rounded-2xl border border-white/10 bg-white/[0.03] hover:border-cyan-500/40 hover:bg-cyan-500/[0.05] overflow-hidden transition-all"
                >
                  {a.featured_image_url ? (
                    <div className="aspect-video overflow-hidden bg-black/40">
                      <img
                        src={a.featured_image_url}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-cyan-500/20 via-slate-900 to-emerald-500/20" />
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3 text-xs">
                      {a.category && (
                        <span className={`font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${categoryClass(a.category)}`}>
                          {a.category}
                        </span>
                      )}
                      <span className="text-gray-500 font-mono">{formatDate(a.published_at)}</span>
                      {a.reading_time_minutes && (
                        <span className="text-gray-500 font-mono">· {a.reading_time_minutes} min read</span>
                      )}
                    </div>
                    <h2 className="text-lg font-semibold text-white group-hover:text-cyan-300 transition-colors line-clamp-2 mb-2">
                      {a.title}
                    </h2>
                    <p className="text-sm text-slate-400 line-clamp-3">
                      {a.excerpt || a.meta_description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
